import React from 'react'
import { Box, DialogContent, Grid, styled, Typography } from '@mui/material'
import { CarmoolaLogo, CloseBrothersLogo, InterstitialClearscoreLogo, InterstitialZutoLogo, MotivLogo } from '@zuto/icons'
import CheckedList from '../../CheckedList'
import AnimatedArrows from '../../Animations/AnimatedArrows'
import TrustBox from '../../TrustPilotHeader/TrustBox'
import { fonts } from '@zuto/theme/src/mui/Typography'

const StyledBlueBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  background: theme.customPalette.primary[90],
  borderRadius: '12px'
}))

const nonPreApprovedlist = [
  'We\'re car finance experts, helping customers since 2006',
  '£0 deposit options available',
  'Get a personalised quote with no impact on your credit score',
  'Your dedicated Car Buying Expert will call you to guide you through the journey'
]

const preApprovedList = [
  'We\'re car finance experts, helping customers since 2006',
  'Get behind the wheel with your guaranteed rate',
  '£0 deposit options available',
  'Your dedicated Car Buying Expert will call you to guide you through the journey'
]

const closeCheckList = [
  'Trusted partner of Close Brothers Motor Finance',
  '£0 deposit options available',
  'Helping UK car buyers since 2006'
]

const headingTextRequiredDetails = 'Transferring you to Zuto, our car finance partner'
const headingTextFormlessJourney = 'Submitting your application to Zuto, our car finance partner'

type FromlessInterstitialContentProps = {
  source: string,
  isPreapproved: boolean,
  isRequiredDetailsV1?: boolean
}

const renderLogo = (source: string) => {
  switch (source) {
    case 'clearscore':
    case 'freedomfinance-t1':
      return <InterstitialClearscoreLogo data-testid="clearscore-logo" />
    case 'motiv':
    case 'motivrefinance':
      return <MotivLogo data-testid="motiv-logo" />
    case 'close':
      return <CloseBrothersLogo data-testid="close-logo" />
    case 'carmoola':
      return <CarmoolaLogo data-testid="carmoola-logo" />
    default:
      return null
  }
}

const renderContent = (source: string, isPreapproved: boolean, headingText: string) => {
  return (
    <DialogContent sx={{ py: 12, textAlign: 'left' }}>
      <Typography sx={{ paddingLeft: 0 }} variant="headlineXS" component="p" align="left" mb={7}>{headingText}</Typography>

      <Grid className="interstitial-header" container spacing={1} columns={3} alignItems="center" justifyContent="space-between" mb={5}>
        <Grid>{renderLogo(source)}</Grid>
        <Grid><AnimatedArrows data-testid="animated-arrows" /></Grid>
        <Grid><InterstitialZutoLogo data-testid="zuto-logo" /></Grid>
      </Grid>
      <Typography variant="rowHeading" sx={{ fontFamily: fonts.bold }} mb={2} align="center">Customers love Zuto&apos;s service</Typography>
      <TrustBox forceMobile={true} />
      <Box mt={2}>
        {!isPreapproved && <Typography sx={{ fontWeight: 500, fontSize: '14px', fontFamily: fonts.medium, paddingTop: 5, paddingBottom: 1 }}>Let&apos;s check your eligibility</Typography>}
        <CheckedList list={isPreapproved ? preApprovedList : nonPreApprovedlist} darkerText />
      </Box>
    </DialogContent>
  )
}

const renderContentCloseBrothers = (source: string) => {
  return (
    <DialogContent sx={{ py: 12, textAlign: 'left' }}>
      <Typography sx={{ paddingLeft: 0 }} variant="headlineXS" component="p" align="left" mb={8}>
        We&apos;re passing your application details over to Zuto.
      </Typography>

      <Grid className="interstitial-header" container spacing={1} columns={3} alignItems="center" justifyContent="space-between" mb={5}>
        <Grid>{renderLogo(source)}</Grid>
        <Grid><AnimatedArrows data-testid="animated-arrows" /></Grid>
        <Grid><InterstitialZutoLogo data-testid="zuto-logo" /></Grid>
      </Grid>
      <Box mt={8} mb={6}>
        <Typography variant="rowHeading" sx={{ fontFamily: fonts.bold }} mb={2} align="left">
          What happens next?
        </Typography>
        <Typography variant="bodyLarge">
          Your dedicated Car Buying Expert will be in touch soon.
        </Typography>
      </Box>
      <Typography variant="rowHeading" sx={{ fontFamily: fonts.bold }} mb={2} align="center">
        Why Customers love Zuto
      </Typography>
      <TrustBox forceMobile={true} />
      <StyledBlueBox mt={6} px={4} pb={4}>
        <CheckedList list={closeCheckList} textVariant="labelLarge" darkerText />
      </StyledBlueBox>
    </DialogContent>
  )
}

const FromlessInterstitialContent = ({
  source,
  isPreapproved,
  isRequiredDetailsV1 = false
}: FromlessInterstitialContentProps): JSX.Element => {
  const headingText = isRequiredDetailsV1 ? headingTextRequiredDetails : headingTextFormlessJourney

  return source !== 'close'
    ? renderContent(source, isPreapproved, headingText)
    : renderContentCloseBrothers(source)
}

export default FromlessInterstitialContent
